import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import {
  useOutOfDatePublicationVariant,
  type UseOutOfDatePublicationVariantArgs,
} from './useOutOfDatePublicationVariant';
import { useOutOfDatePublicationAction } from './useOutOfDatePublicationAction';
import { ACTION_KEYS } from './copyKeys';

export interface OutOfDatePublicationCompactButtonProps
  extends UseOutOfDatePublicationVariantArgs {
  /** Regenerates the media. */
  onRegenerate: () => Promise<void>;
  /** Updates the live publication. */
  onUpdate: () => Promise<void>;
  className?: string;
}

/**
 * Compact stand-in for `OutOfDatePublicationWidget`, rendered inline in the
 * page's action row (next to Share) once the user has dismissed the
 * floating widget for this project. Clicking it runs the action directly —
 * there's no confirmation step, since dismissing the widget already meant
 * "later, not never".
 *
 * The caller is expected to also clear its persisted dismissal flag inside
 * `onRegenerate`/`onUpdate` once the mutation succeeds, so if the project
 * goes stale again afterwards the floating widget comes back instead of
 * staying compact. This component only decides *which* variant/label to
 * show, not the dismissal bookkeeping — see `OutOfDatePublicationWidget`.
 */
export const OutOfDatePublicationCompactButton = ({
  isLoading,
  isItemsOutOfDate,
  isPublicationOutOfDate,
  onRegenerate,
  onUpdate,
  className,
}: OutOfDatePublicationCompactButtonProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const variant = useOutOfDatePublicationVariant({
    isLoading,
    isItemsOutOfDate,
    isPublicationOutOfDate,
  });
  const { isSubmitting, runAction } = useOutOfDatePublicationAction({
    variant,
    onRegenerate,
    onUpdate,
  });

  if (!variant) return null;

  return (
    <Button
      className={className}
      variant="upgrade"
      size="s"
      loading={isSubmitting}
      disabled={isSubmitting}
      onClick={runAction}
    >
      {t(ACTION_KEYS[variant])}
    </Button>
  );
};

export default OutOfDatePublicationCompactButton;
